import { useState, useMemo, SyntheticEvent } from "react";
import { Bar } from "react-chartjs-2";
import { tabPanelClasses, TabContext, TabPanel } from "@mui/lab";
import { CardContent, Tab, Box, Typography as T, Tooltip } from "@mui/material";
import { ChartOptions } from "chart.js";
import { SiteDetails, BehaviorCode } from "catalog/types/interfaces/conceptResponse";
import ContainedTabs from "~/components/ContainedTabs";
import FlatCard from "~/components/FlatCard";
import { COLOR_PALETTE } from "~/customer/chartJS/colors";
import InfoIcon from "~/pages/catalog/components/InfoIcon";
import { DEFAULT_OPTS, isHighCardinalityStackedBar } from "~/pages/catalog/utils/chart";
import { merge } from "chart.js/helpers";

const compactFormatter = new Intl.NumberFormat("en", { notation: "compact" });

const CHART_OPTS: ChartOptions<"bar"> = merge<ChartOptions<"bar">, ChartOptions<"bar">>(
  {
    indexAxis: "y",
    scales: {
      x: {
        grid: { display: false },
        stacked: true,
        title: {
          display: true,
          text: "Participant Count",
        },
        ticks: {
          callback: (value) => {
            if (!isNaN(Number(value))) {
              return compactFormatter.format(Number(value));
            }
            return value;
          },
        },
      },
      y: {
        stacked: true,
      },
    },
    plugins: {
      colors: {
        forceOverride: true,
      },
      legend: {
        position: "bottom",
        labels: {
          usePointStyle: true,
          pointStyle: "rect",
        },
      },
      tooltip: {
        displayColors: false,
      },
    },
  },
  DEFAULT_OPTS
);

const getChartData = (type: "gender_counts" | "race_counts", data: BehaviorCode[]) => {
  /**
   * Genders/Races with zero values are omitted in the API. Need to first collect all
   * possible label values so we can pad the dataset appropriately when there are gaps
   */
  const datasetLabels = data.reduce<string[]>((acc, code) => {
    const tmp: string[] = [];
    code[type].forEach((b) => {
      if (acc.indexOf(b.concept_name) === -1) {
        tmp.push(b.concept_name);
      }
    });
    return [...acc, ...tmp];
  }, []);

  /**
   * For each label, pull the corresponding count for each status code,
   * padding where data is missing for a particular label
   */
  const datasets = datasetLabels.map((label, i) => {
    const counts: number[] = [];
    data.forEach((code) => {
      const target = code[type].find((b) => b.concept_name === label);
      if (target) {
        counts.push(target.participant_count);
      } else {
        counts.push(0);
      }
    });

    return {
      label,
      data: counts,
      backgroundColor: COLOR_PALETTE?.[i] ?? "#ccc",
    };
  });

  return {
    labels: data.map(({ behavior_code_name }) => behavior_code_name),
    datasets,
  };
};

const BehaviorCodeChart = (props: { data: SiteDetails }) => {
  const { behavior_code_breakdown } = props.data;
  const [chartTab, setChartTab] = useState<"race" | "gender">("gender");

  const raceData = useMemo(
    () => getChartData("race_counts", behavior_code_breakdown),
    [behavior_code_breakdown]
  );

  const genderData = useMemo(
    () => getChartData("gender_counts", behavior_code_breakdown),
    [behavior_code_breakdown]
  );

  const isHighCardinality = isHighCardinalityStackedBar(raceData, genderData);

  return (
    <FlatCard
      sx={{
        gridColumn: isHighCardinality ? "span 2" : "span 1",
        [`.${tabPanelClasses.root}`]: { p: 1 },
      }}
    >
      <CardContent>
        <TabContext value={chartTab}>
          <Box display="flex" alignItems="center" justifyContent="space-between" gap={0.5} pb={0.5}>
            <T
              variant="body1"
              fontWeight={600}
              overflow="hidden"
              textOverflow="ellipsis"
              whiteSpace="nowrap"
            >
              Malignancy
            </T>
            <Tooltip arrow title="A breakdown of cancer participants by behavior codes.">
              <InfoIcon />
            </Tooltip>
          </Box>

          <Box display="flex" justifyContent="center" mt={1}>
            <ContainedTabs
              value={chartTab}
              onChange={(_: SyntheticEvent, value: any) => setChartTab(value)}
            >
              <Tab label="Gender" value="gender" />
              <Tab label="Race" value="race" />
            </ContainedTabs>
          </Box>
          <TabPanel className="chart-container" value="race">
            <Bar data={raceData} options={CHART_OPTS} />
          </TabPanel>
          <TabPanel className="chart-container" value="gender">
            <Bar data={genderData} options={CHART_OPTS} />
          </TabPanel>
        </TabContext>
      </CardContent>
    </FlatCard>
  );
};

export default BehaviorCodeChart;
